"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useScrollLock = useScrollLock;
const react_1 = require("react");
/**
 * Hook to lock/unlock page scrolling (useful for modals, lightboxes, etc.)
 * Sets a CSS variable --scrollbar-width that can be used to prevent layout shift.
 *
 * @param isLocked - Whether scrolling should be locked
 *
 * @example
 * ```tsx
 * function Modal({ isOpen }: { isOpen: boolean }) {
 *   useScrollLock(isOpen);
 *   return isOpen ? <div>Modal content</div> : null;
 * }
 * ```
 */
function useScrollLock(isLocked) {
    const lockScroll = (0, react_1.useCallback)(() => {
        const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
        // Set a CSS variable for the scrollbar width (can be used to prevent layout shift)
        document.documentElement.style.setProperty('--scrollbar-width', `${scrollBarWidth}px`);
        // Lock scrolling on the document element
        document.documentElement.style.overflowY = 'hidden';
        document.documentElement.style.scrollBehavior = 'auto';
    }, []);
    const unlockScroll = (0, react_1.useCallback)(() => {
        // Clear the CSS variable and styles
        document.documentElement.style.overflowY = '';
        document.documentElement.style.removeProperty('--scrollbar-width');
        document.documentElement.style.scrollBehavior = '';
    }, []);
    (0, react_1.useEffect)(() => {
        if (isLocked) {
            lockScroll();
            window.history.scrollRestoration = 'manual';
        }
        else {
            unlockScroll();
            window.history.scrollRestoration = 'auto';
        }
        return () => {
            unlockScroll();
        };
    }, [isLocked, lockScroll, unlockScroll]);
}
